import * as React from 'react';

import {LegacyMeasurement as MeasurementType} from 'constants/MeasurementConstants';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRenderer,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {Measurement} from 'samples/transfers/Measurements';
import {ListableGroup, SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import * as ScrollableTableConstants from 'ui-library/scrollable/ScrollableTableConstants';
import {BasicEvent} from 'util/Types';

// Keep in sync with benchling/search/engine/elasticsearch_engine/group_by/sum_aggregate_builder.py
const MIXED_UNITS_SENTINEL = 'MIXED_UNITS';

function QuantitySumAggregateValue(props: {measurement: MeasurementType}): JSX.Element {
  return (
    <span>
      <span className="u-gray-darker">Sum: </span>
      {props.measurement.units === MIXED_UNITS_SENTINEL ? (
        <span className="u-faded">Mixed units</span>
      ) : (
        <Measurement measurement={props.measurement} />
      )}
    </span>
  );
}

// GroupListingRenderer does not extend from BaseListingRendererWithPlugins because it does not map to a
// ModelTypeConstant type, and so can't call "pluginApplies". This is acceptable as groups are not models
// and will never implement platform capabilities.
export default class GroupListingRenderer implements ListingRenderer<ListingRendererType.GROUP> {
  renderIcon(): JSX.Element {
    // Groups are values shared across all child rows, not items, so do not have an icon.
    return <></>;
  }

  getColumnConfigurations(groups: Array<ListableGroup>): Array<ColumnConfiguration<ListableGroup>> {
    return [
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          accessorKey: ColumnAccessorKey.NAME,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
        },
        render: (group: ListableGroup) => {
          const tooltipText = `${group.label} - Count: ${group.count}`;
          return (
            <span className="u-lr-split">
              <Tooltip title={tooltipText} container="body" placement={TooltipPlacement.TOP} delay={350}>
                <span className="u-ellipsis-text">{group.label}</span>
              </Tooltip>
              <Tooltip title={tooltipText} container="body" placement={TooltipPlacement.TOP} delay={350}>
                <span className="u-spacing-left">
                  <span className="u-gray-darker">Count:</span> {group.count}
                </span>
              </Tooltip>
            </span>
          );
        },
      },
      ...this._makeAggregateColumns(groups),
    ];
  }

  getRowContentHeight(group: ListableGroup): number {
    return ScrollableTableConstants.TABLE_ROW_BODY_HEIGHT;
  }

  openContextMenu(group: ListableGroup, e: BasicEvent, extras: unknown): void {}

  _makeAggregateColumns(groups: Array<ListableGroup>): Array<ColumnConfiguration<ListableGroup>> {
    if (groups.length === 0) {
      return [];
    }

    // Today we always return homogenous groups, so they will all have the same aggregate fields.
    // Therefore, we can simply use the first group as a representative example to determine which
    // columns to include. If we return different types of groups in the future (e.g. to support
    // multi-level grouping), this will no longer hold.
    const aggregates = groups[0].aggregates;

    const aggregateColumns: Array<ColumnConfiguration<ListableGroup>> = [];

    for (const key of Object.keys(aggregates)) {
      switch (key) {
        case 'quantity':
          aggregateColumns.push({
            column: {
              header: getQuantityDisplayString(),
              accessorKey: ColumnAccessorKey.VOLUME,
              isCollapsible: false,
              isSortable: false,
            },
            render: (group: ListableGroup) => (
              <QuantitySumAggregateValue measurement={group.aggregates[key] as MeasurementType} />
            ),
            isHidden: () => false,
          });
          break;
        default:
          // It's not an error for the server to return an aggregate that we don't decide to
          // display, so simply noop if we find a key not covered above.
          break;
      }
    }

    return aggregateColumns;
  }
}
